/**
 * Photo Diagnostic — intro modal.
 *
 * Educational modal explaining what the feature does and what to
 * photograph. Opened from the home-grid bold card OR mini card.
 *
 * The primary CTA (`התחל לאבחן עכשיו`) closes this modal and opens the
 * AI chat with the photo picker auto-triggered, so the user lands
 * directly in the camera/file picker without any extra taps.
 *
 * Privacy note rendered in the modal: images are sent to Claude Vision
 * for one-shot analysis and not persisted by AutoMe (matching the actual
 * behavior on the server side — askAutoMeAI doesn't store images, only
 * the text reply).
 */

import { createLogger } from '../../core/logger.js';

const log = createLogger('photo-diag.intro');

const OVERLAY_ID = 'photoDiagIntroOverlay';

/** Open the intro modal. Idempotent. */
export function open() {
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  overlay.classList.add('open');
}

function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
}

/** Close the intro and open the AI chat with the photo picker firing. */
function startDiagnostic() {
  close();
  const w = /** @type {any} */ (window);
  if (typeof w.openAiChat === 'function') {
    w.openAiChat(null, { autoTriggerPhoto: true });
  } else {
    log.warn('openAiChat global missing');
  }
}

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'pdiag-overlay';
  ov.innerHTML = `
    <aside class="pdiag-modal" role="dialog" aria-modal="true" aria-labelledby="pdiagTitle">
      <header class="pdiag-header">
        <div class="pdiag-title-block">
          <span class="pdiag-icon">🔬</span>
          <div>
            <div class="pdiag-title" id="pdiagTitle">אבחון תקלה ב־AI</div>
            <div class="pdiag-sub">צלם → קבל אבחון תוך 15 שניות</div>
          </div>
        </div>
        <button type="button" class="pdiag-close" aria-label="סגור">✕</button>
      </header>

      <div class="pdiag-body">

        <section class="pdiag-section">
          <div class="pdiag-section-title">✅ מה אפשר לצלם?</div>
          <div class="pdiag-items">
            <div class="pdiag-item">
              <span class="pdiag-item-icon">🚦</span>
              <div class="pdiag-item-body">
                <div class="pdiag-item-title">חיווי בדשבורד</div>
                <div class="pdiag-item-desc">שמן, בלמים, ABS, Check Engine, חום</div>
              </div>
            </div>
            <div class="pdiag-item">
              <span class="pdiag-item-icon">💧</span>
              <div class="pdiag-item-body">
                <div class="pdiag-item-title">נזילה מתחת לרכב</div>
                <div class="pdiag-item-desc">שמן מנוע, מצנן, נוזל בלמים</div>
              </div>
            </div>
            <div class="pdiag-item">
              <span class="pdiag-item-icon">🛠️</span>
              <div class="pdiag-item-body">
                <div class="pdiag-item-title">נזק, שריטה או חלודה</div>
                <div class="pdiag-item-desc">הערכת חומרה ועלות תיקון</div>
              </div>
            </div>
            <div class="pdiag-item">
              <span class="pdiag-item-icon">❓</span>
              <div class="pdiag-item-body">
                <div class="pdiag-item-title">חלק שלא מזהים</div>
                <div class="pdiag-item-desc">כל חלק ברכב שתוהים מהו</div>
              </div>
            </div>
          </div>
        </section>

        <section class="pdiag-section">
          <div class="pdiag-section-title">🎯 מה תקבל בתשובה?</div>
          <div class="pdiag-results">
            <div class="pdiag-result">🔍 <strong>זיהוי</strong> — מה רואים בתמונה</div>
            <div class="pdiag-result">⚠️ <strong>אבחון</strong> — סיבות אפשריות</div>
            <div class="pdiag-result">🚦 <strong>דחיפות</strong> — אדום / צהוב / ירוק</div>
            <div class="pdiag-result">💰 <strong>עלות תיקון</strong> — טווח ריאלי בישראל</div>
            <div class="pdiag-result">🔧 <strong>הוראות</strong> — מה לעשות עכשיו</div>
          </div>
        </section>

        <section class="pdiag-section">
          <div class="pdiag-section-title">📝 איך זה עובד?</div>
          <div class="pdiag-steps">
            <div class="pdiag-step">
              <span class="pdiag-step-num">1</span>
              <span class="pdiag-step-text">צלם תמונה ברורה של מה שמטריד</span>
            </div>
            <div class="pdiag-step">
              <span class="pdiag-step-num">2</span>
              <span class="pdiag-step-text">שלח ל־AutoMe AI</span>
            </div>
            <div class="pdiag-step">
              <span class="pdiag-step-num">3</span>
              <span class="pdiag-step-text">קבל תשובה מובנית תוך שניות</span>
            </div>
          </div>
        </section>

        <div class="pdiag-privacy">
          🔒 <strong>פרטיות:</strong> התמונה לא נשמרת אצלנו — היא נשלחת לאבחון
          חד-פעמי בלבד דרך Claude AI. רק תשובת הטקסט נשמרת בהיסטוריית השיחה.
        </div>

        <button type="button" class="pdiag-cta" id="pdiagStartBtn">
          <span class="pdiag-cta-icon">📸</span>
          <span class="pdiag-cta-text">התחל לאבחן עכשיו</span>
          <span class="pdiag-cta-arrow">←</span>
        </button>

      </div>
    </aside>
  `;

  ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
  ov.querySelector('.pdiag-close')?.addEventListener('click', close);
  ov.querySelector('#pdiagStartBtn')?.addEventListener('click', startDiagnostic);
  return ov;
}
